import { PromiseState } from 'react-refetch';
import { string, func, number, array, instanceOf } from 'prop-types';
import { compose } from 'recompose';

import detectIt from 'detect-it';

import entityPropType from 'propTypes/entityTypes';

import LoadingBar from 'components/styled/LoadingBar/LoadingBar';
import NoResults from 'components/utility/NoResults/NoResults';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';

import apiConnect from 'helpers/apiConnect';
import pluralize from 'helpers/pluralize';

import {
  SearchResults,
  SuggestedTitle,
  LoadingBarWrapper,
  StyledErrorBoundary,
  StyledSearchResultsLoader,
  NoResultsSpacer,
  SearchResult,
  StyledEntityItem,
} from './CompareSearchResults.style';
import getSearchURL from 'url-constructors/getSearchURL';

const { hasTouch, hasMouse } = detectIt;

const smoothScrolling = hasTouch && !hasMouse;

/** Render all the search results, or if there are not show an empty state. */
const renderSearchResults = ({
  value,
  entityIds,
  getItemProps,
  highlightedIndex,
  searchValue,
}) => {
  if (!value) {
    return null;
  }

  if (value.length === 0) {
    return (
      <NoResultsSpacer>
        <NoResults searchValue={searchValue} />
      </NoResultsSpacer>
    );
  }

  return (
    value
      /** Remove the items that are already being compared from the results. */
      .filter((item) => {
        return item && !entityIds.includes(item.id);
      })
      .map((item, index) => {
        return (
          <SearchResult
            key={`compare-search-result-${item.id}`}
            {...getItemProps({
              key: `compare-search-result-${item.id}`,
              index,
              item,
              isHighlighted: highlightedIndex === index,
            })}
            data-pw="compare-search-result-item"
          >
            <StyledEntityItem
              entityId={item.id}
              entityType={item.type}
              displayName={item.displayName}
              primaryArtist={item.primaryArtist}
              thumbnailUrl={item.thumbnailUrl}
              releaseDate={item.releaseDate}
              isGroup={item.is_group}
              parentEntityId={item.parent_group_id}
              groupChildCount={item.group_child_count}
              releaseCount={item.grouped_album_count}
              artistCount={item.artist_count}
              trackCount={item.grouped_track_count}
              entityData={item.data}
              artworkHasBackground={true}
            />
          </SearchResult>
        );
      })
  );
};

const CompareSearchResults = (props) => {
  const {
    filteredFollowingCount,
    entityType,
    filteredFollowingEntities,
    entityIds,
    getItemProps,
    highlightedIndex,
    searchFetch,
    searchValue,
  } = props;

  const { pending, fulfilled, reason, value } = searchFetch;

  return (
    <>
      {searchValue === '' && filteredFollowingCount.length > 0 && (
        /** Display the following entities as default placeholder if the user is following any. */
        <SearchResults
          smoothScrolling={smoothScrolling}
          data-pw="compare-search-results"
        >
          <SuggestedTitle>Suggested {pluralize(entityType)}</SuggestedTitle>
          {filteredFollowingEntities.length > 0 ? (
            renderSearchResults({
              searchValue,
              value: filteredFollowingEntities,
              entityIds,
              getItemProps,
              highlightedIndex,
            })
          ) : (
            <StyledSearchResultsLoader
              entityType={entityType}
              itemCount={filteredFollowingCount.length}
            />
          )}
        </SearchResults>
      )}
      {searchValue !== '' && (
        /** If there is a searchvalue display the searchResults */
        <SearchResults
          smoothScrolling={smoothScrolling}
          data-pw="compare-search-results"
        >
          <StyledErrorBoundary
            error={reason}
            errorInfo={reason ? reason.cause : null}
          >
            <LoadingBarWrapper>
              <LoadingBar loading={pending} />
            </LoadingBarWrapper>

            {pending && (
              /** Show loading placeholder when there is no data yet */
              <StyledSearchResultsLoader entityType={entityType} />
            )}
            {fulfilled && (
              <AnimatedLoadingWrapper loading={pending}>
                {renderSearchResults({
                  value,
                  entityIds,
                  getItemProps,
                  highlightedIndex,
                  searchValue,
                })}
              </AnimatedLoadingWrapper>
            )}
          </StyledErrorBoundary>
        </SearchResults>
      )}
    </>
  );
};

CompareSearchResults.propTypes = {
  filteredFollowingCount: array.isRequired,
  entityType: entityPropType.isRequired,
  filteredFollowingEntities: array.isRequired,
  entityIds: array.isRequired,
  getItemProps: func.isRequired,
  highlightedIndex: number,
  searchFetch: instanceOf(PromiseState).isRequired,
  searchValue: string.isRequired,
};

export default compose(
  apiConnect((props) => {
    const { searchValue, entityType } = props;

    const apiUrl = getSearchURL({
      entityType,
      limit: 50,
      query: searchValue,
    });

    return { searchFetch: apiUrl };
  })
)(CompareSearchResults);
